import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Home, Repeat, Instagram, Music2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const fmt = (n) => (n || 0).toLocaleString();

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    status: "checking",
    payment_status: "",
    amount_total: 0,
    currency: "usd",
    source: null,
    panel_order_id: null,
    platform: null,
    followers: null,
    instagram_username: null,
  });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "missing" }));
      return;
    }
    let canceled = false;
    const poll = async () => {
      if (canceled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const data = res.data;
        setState((prev) => ({ ...prev, ...data }));
        if (data.payment_status === "paid" && (data.source !== "reseller" || data.panel_order_id)) {
          return; // fully done — regular order OR reseller with panel id
        }
        if (data.status === "expired") return;
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      canceled = true;
    };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  const failed = state.status === "timeout" || state.status === "missing";
  const isReseller = state.source === "reseller";
  const isTikTok = state.platform === "tiktok";
  const dispatching = paid && isReseller && !state.panel_order_id;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <div
          data-testid="success-card"
          className="max-w-lg w-full text-center bg-[#0A0A0A] border border-white/10 rounded-2xl p-10"
        >
          {paid ? (
            <>
              <div
                className={`w-16 h-16 rounded-full grid place-items-center mx-auto mb-6 ${
                  isTikTok
                    ? "bg-[#00F2EA]/10 glow-tiktok"
                    : "bg-[#FF007F]/10 glow-pink"
                }`}
              >
                <CheckCircle2
                  className={`w-9 h-9 ${
                    isTikTok ? "text-[#00F2EA]" : "text-[#FF007F]"
                  }`}
                />
              </div>
              <h1 className="font-display text-4xl font-black tracking-tighter mb-3">
                Payment{" "}
                <span className={isTikTok ? "text-gradient-tiktok" : "text-gradient-neon"}>
                  confirmed
                </span>
              </h1>

              {isReseller ? (
                <>
                  <p className="text-white/70 leading-relaxed">
                    {dispatching
                      ? "Payment received. Dispatching your order to the panel now…"
                      : "Order sent to the panel — delivery has started."}
                  </p>
                  <div className="mt-5 bg-white/5 border border-white/10 rounded-xl p-4 space-y-2 text-sm">
                    {state.instagram_username && (
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 inline-flex items-center gap-1.5">
                          {isTikTok ? <Music2 className="w-3.5 h-3.5" /> : <Instagram className="w-3.5 h-3.5" />}
                          Target
                        </span>
                        <span className="text-white font-mono">@{state.instagram_username}</span>
                      </div>
                    )}
                    {state.followers ? (
                      <div className="flex items-center justify-between">
                        <span className="text-white/50">Followers</span>
                        <span className="text-white">{fmt(state.followers)}</span>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between">
                      <span className="text-white/50">Amount paid</span>
                      <span className="text-white font-semibold">
                        ${state.amount_total.toFixed(2)}
                      </span>
                    </div>
                    {state.panel_order_id ? (
                      <div className="flex items-center justify-between">
                        <span className="text-white/50">Panel order</span>
                        <span
                          className={`font-mono ${
                            isTikTok ? "text-[#00F2EA]" : "text-[#FF007F]"
                          }`}
                        >
                          #{state.panel_order_id}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-white/50">Panel order</span>
                        <span className="inline-flex items-center gap-2 text-white/60">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…
                        </span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-white/70 leading-relaxed">
                    Your order is locked in. Delivery starts within a few hours and will continue gradually.
                  </p>
                  <div className="mt-6 text-white/50 text-sm">
                    Amount paid:{" "}
                    <span className="text-white font-semibold">
                      ${state.amount_total.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </>
          ) : expired || failed ? (
            <>
              <div className="w-16 h-16 rounded-full bg-white/5 grid place-items-center mx-auto mb-6">
                <XCircle className="w-9 h-9 text-white/70" />
              </div>
              <h1 className="font-display text-3xl font-black mb-3">Payment not completed</h1>
              <p className="text-white/60">
                {state.status === "missing"
                  ? "No checkout session found in this URL."
                  : "We couldn't confirm your payment. If you believe this is a mistake, please contact support."}
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-white/5 grid place-items-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
              </div>
              <h1 className="font-display text-3xl font-black mb-3">Confirming your payment…</h1>
              <p className="text-white/60">Hang tight — this usually takes a few seconds.</p>
            </>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            {isReseller ? (
              <Link
                to="/reseller"
                data-testid="success-reseller-btn"
                className={`inline-flex items-center gap-2 text-white font-bold px-6 py-3 rounded-full hover:scale-[1.02] transition-transform ${
                  isTikTok ? "bg-gradient-tiktok glow-tiktok" : "bg-neon glow-pink"
                }`}
              >
                <Repeat className="w-4 h-4" /> Submit another
              </Link>
            ) : null}
            <Link
              to="/"
              data-testid="success-home-btn"
              className="inline-flex items-center gap-2 bg-white text-black font-bold px-6 py-3 rounded-full hover:bg-white/90 transition"
            >
              <Home className="w-4 h-4" /> Back to home
            </Link>
          </div>

          {sessionId && (
            <div className="mt-8 text-[10px] text-white/30 break-all">
              Session: {sessionId}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
